import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiTrendingUp,
  FiShoppingBag,
  FiCalendar,
  FiChevronDown,
  FiBarChart2,
  FiClock,
} from "react-icons/fi";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
  LabelList,
} from "recharts";
import IndiaMap from "react-datamaps-india";
import { useQuery } from "react-query";
import { getData } from "../utils/api";
import { formatAmount, formatAmountLegacy } from "../utils/formatters";
import {
  format,
  parseISO,
  subDays,
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfWeek,
  endOfWeek,
} from "date-fns";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const PLATFORM_COLORS = ["#FF9900", "#2874F0", "#00A5CF", "#FF6B6B", "#6C757D"];

// Date range options
const getDateRangeOptions = () => {
  const today = new Date();
  const lastWeekStart = startOfWeek(subDays(today, 7));
  const lastWeekEnd = endOfWeek(subDays(today, 7));
  const lastMonthStart = startOfMonth(subMonths(today, 1));
  const lastMonthEnd = endOfMonth(subMonths(today, 1));
  const thisMonthStart = startOfMonth(today);
  const last7DaysStart = subDays(today, 6);
  const last30DaysStart = subDays(today, 29);
  const last60DaysStart = subDays(today, 59);
  const last90DaysStart = subDays(today, 89);

  return [
    {
      label: `Last Week (${format(lastWeekStart, "MMM dd")} - ${format(
        lastWeekEnd,
        "MMM dd"
      )})`,
      startDate: lastWeekStart,
      endDate: lastWeekEnd,
    },
    {
      label: `Last Month (${format(lastMonthStart, "MMM dd")} - ${format(
        lastMonthEnd,
        "MMM dd"
      )})`,
      startDate: lastMonthStart,
      endDate: lastMonthEnd,
    },
    {
      label: `This Month (${format(thisMonthStart, "MMM dd")} - ${format(
        today,
        "MMM dd"
      )})`,
      startDate: thisMonthStart,
      endDate: today,
    },
    {
      label: `Last 7 Days (${format(last7DaysStart, "MMM dd")} - ${format(
        today,
        "MMM dd"
      )})`,
      startDate: last7DaysStart,
      endDate: today,
    },
    {
      label: `Last 30 Days (${format(last30DaysStart, "MMM dd")} - ${format(
        today,
        "MMM dd"
      )})`,
      startDate: last30DaysStart,
      endDate: today,
    },
    {
      label: `Last 60 Days (${format(last60DaysStart, "MMM dd")} - ${format(
        today,
        "MMM dd"
      )})`,
      startDate: last60DaysStart,
      endDate: today,
    },
    {
      label: `Last 90 Days (${format(last90DaysStart, "MMM dd")} - ${format(
        today,
        "MMM dd"
      )})`,
      startDate: last90DaysStart,
      endDate: today,
    },
    {
      label: "Custom Date Range",
      startDate: null,
      endDate: null,
      isCustom: true,
    },
  ];
};

const StatCard = ({ title, value, icon, trend }) => (
  <div className="stat-card p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-base-content/60">{title}</p>
        <h3 className="text-2xl font-bold mt-2">{value}</h3>
        {trend && (
          <p
            className={`text-xs mt-2 ${
              trend > 0 ? "text-success" : "text-error"
            }`}
          >
            {trend > 0 ? "+" : ""}
            {trend.toFixed(2)}% from last 30 days
          </p>
        )}
      </div>
      <div className="p-3 bg-primary/10 rounded-lg">{icon}</div>
    </div>
  </div>
);


const TopSellingProducts = ({ data, startDate, endDate }) => {
  const navigate = useNavigate();

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">Top Selling Products</h3>
      <div className="space-y-3">
        {data.map((product, index) => (
          <div
            key={index}
            className="flex items-center justify-between p-3 bg-base-200 rounded-lg cursor-pointer hover:bg-base-300 transition-colors"
            onClick={() => {
              const skuForUrl = product.sku || product.channelSku || product.productName;
              navigate(
                `/analytics/product-detail/${encodeURIComponent(
                  skuForUrl
                )}`,
                {
                  state: {
                    productName: product.productName,
                    useSku: !!(product.sku || product.channelSku),
                    startDate: startDate.toLocaleDateString("en-CA"),
                    endDate: endDate.toLocaleDateString("en-CA"),
                    fromPage: "/dashboard",
                  },
                }
              );
            }}
          >
            <div className="flex-1 min-w-0">
              <p
                className="font-medium line-clamp-2"
                title={product.productName}
              >
                {product.productName}
              </p>
              <p className="text-sm text-base-content/60">
                {product.unitSold} units sold
              </p>
            </div>
            <p className="font-semibold ml-3 flex-shrink-0">
              {formatAmount(product.revenueCollected)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

const IndiaMapComponent = ({ dataType, setDataType, stateData }) => {
  // Normalize API state names to library-expected map keys
  const normalizeStateName = (rawName) => {
    if (!rawName) return null;
    const name = String(rawName).trim().toLowerCase();
    if (name === "n/a" || name === "na" || name === "unknown") return null;
    const special = {
      "tamilnadu": "Tamil Nadu",
      "pondicherry": "Puducherry",
      "jammu & kashmir": "Jammu & Kashmir",
      "arunachal pradesh": "Arunanchal Pradesh", // library key spelling
      "andaman & nicobar islands": "Andaman & Nicobar Island",
      "andaman and nicobar islands": "Andaman & Nicobar Island",
      "dadra and nagar haveli and daman and diu": "Dadra & Nagar Haveli",
      "dadra & nagar haveli and daman & diu": "Dadra & Nagar Haveli",
    };
    if (special[name]) return special[name];
    // Generic Title Case for most states
    return name
      .split(/\s+/)
      .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : w))
      .join(" ");
  };

  // Transform array to object keyed by normalized state name
  const stateDataObj = (stateData || []).reduce((acc, curr) => {
    const norm = normalizeStateName(curr.state);
    if (!norm) return acc; // skip unknowns
    acc[norm] = {
      orders: curr.total_orders || 0,
      sales: curr.total_sales || 0,
    };
    return acc;
  }, {});

  // The library automatically generates colors based on values
  // It uses startColor and endColor to create a gradient from min to max values
  const mapData = Object.entries(stateDataObj).reduce((acc, [state, data]) => {
    const value = data[dataType];
    // Ensure value is a valid number (not null/undefined)
    acc[state] = {
      value: value !== null && value !== undefined ? Number(value) : 0,
    };
    return acc;
  }, {});

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">
          State-wise {dataType === "orders" ? "Orders" : "Sales"} 
        </h3>
        <div className="flex gap-2">
          <button
            className={`btn btn-sm ${
              dataType === "orders" ? "btn-primary" : "btn-ghost"
            }`}
            onClick={() => setDataType("orders")}
          >
            Orders
          </button>
          <button
            className={`btn btn-sm ${
              dataType === "sales" ? "btn-primary" : "btn-ghost"
            }`}
            onClick={() => setDataType("sales")}
          >
            Sales
          </button>
        </div>
      </div>

      <div
        className="h-[400px] m-auto  max-w-md flex items-center justify-center"
        style={{ position: "relative" }}
      >
        <div className="w-full max-w-md">
          <IndiaMap
            regionData={mapData}
            hoverComponent={({ value }) => {
              return (
                <div>
                  <div>
                    <p className="font-semibold">{value.name}</p>
                    <p className="text-sm">
                      {dataType === "orders" ? `Orders: ${value.value}` : `Sales: ${formatAmountLegacy(value.value)}`}
              
                    </p>
                  </div>
                </div>
              );
            }}
            mapLayout={{
              title: "",
              legendTitle: "",
              startColor: "#e0f2fe",
              endColor: "#0ea5e9",
              hoverTitle: "Count",
              noDataColor: "#e0f2fe",
            }}
          />
        </div>
      </div>
    </div>
  );
};

const AmazonAdsPerformance = ({ amazonAdsData }) => (
  <div className="dashboard-card p-6">
    <h3 className="text-lg font-semibold mb-4">Amazon Ads Performance</h3>
    <div className="h-80">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={amazonAdsData}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" />
          <YAxis dataKey="name" type="category" />
          <Tooltip
            formatter={(value) => [`₹${value.toLocaleString()}`, ""]}
            contentStyle={{
              backgroundColor: "white",
              border: "1px solid #ccc",
            }}
          />
          <Bar dataKey="value" fill="#0ea5e9">
            {amazonAdsData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={index === 0 ? "#ef4444" : "#22c55e"}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const PlatformSales = ({ data }) => {
  const RADIAN = Math.PI / 180;
  const renderPieLabel = ({ cx, cy, midAngle, outerRadius, value }) => {
    if (value == null) return null;
    const label = formatAmount(value);
    const radius = outerRadius * 0.72;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const labelWidth = label.length * 7 + 8;
    const labelHeight = 14;
    const rectX = x - labelWidth / 2;
    const rectY = y - labelHeight / 2;
    return (
      <g>
        <rect
          x={rectX}
          y={rectY}
          width={labelWidth}
          height={labelHeight}
          rx={3}
          ry={3}
          fill="rgba(255,255,255,0.9)"
          stroke="#e5e7eb"
        />
        <text
          x={x}
          y={rectY + labelHeight - 4}
          fill="#374151"
          fontSize={10}
          textAnchor="middle"
        >
          {label}
        </text>
      </g>
    );
  };

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">
        Platform-wise Sales Distribution
      </h3>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderPieLabel}
              outerRadius={100}
              fill="#8884d8"
              dataKey="revenueCollected"
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={PLATFORM_COLORS[index % PLATFORM_COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, name, props) => [
                formatAmountLegacy(value),
                props.payload.platform || "Portal",
              ]}
              contentStyle={{
                backgroundColor: "white",
                border: "1px solid #ccc",
              }}
            />
            <Legend formatter={(value) => data[value].platform} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [dataType, setDataType] = useState("orders");
  const [selectedDateRange, setSelectedDateRange] = useState(3); // Default to "This Month"
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(new Date());
  const [customEndDate, setCustomEndDate] = useState(new Date());
  const [appliedCustomStartDate, setAppliedCustomStartDate] = useState(
    new Date()
  );
  const [appliedCustomEndDate, setAppliedCustomEndDate] = useState(new Date());

  const dateRangeOptions = getDateRangeOptions();
  const currentDateRange = dateRangeOptions[selectedDateRange];

  const startDate = currentDateRange.isCustom
    ? appliedCustomStartDate
    : currentDateRange.startDate;
  const endDate = currentDateRange.isCustom
    ? appliedCustomEndDate
    : currentDateRange.endDate;

  const {
    data: dashboardData,
    isLoading,
    error,
  } = useQuery(
    [
      "dashboardData",
      format(startDate, "yyyy-MM-dd"),
      format(endDate, "yyyy-MM-dd"),
    ],
    () =>
      getData(
        `/data/order/home-page-new?startDate=${format(
          startDate,
          "yyyy-MM-dd"
        )}&endDate=${format(endDate, "yyyy-MM-dd")}`
      ),
    {
      onError: (error) => {
        console.error("Dashboard Data Error:", error);
        toast.error(error.message || "Failed to load dashboard data");
      },
      staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
      cacheTime: 10 * 60 * 1000, // Keep data in cache for 10 minutes
      refetchOnWindowFocus: false, // Prevent refetch on window focus
      refetchOnMount: false, // Prevent refetch on component mount
    }
  );

  const handleDateRangeChange = (index) => {
    if (dateRangeOptions[index].isCustom) {
      setShowCustomDateModal(true);
    } else {
      setSelectedDateRange(index);
    }
  };

  const handleCustomDateSubmit = () => {
    setAppliedCustomStartDate(customStartDate);
    setAppliedCustomEndDate(customEndDate);
    setShowCustomDateModal(false);
    setSelectedDateRange(7); // Custom date range index
  };


  function formatDate(isoString) {
    const date = new Date(isoString);

    const options = {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };

    return date.toLocaleString("en-US", options);
  }

  const formatChartDate = (dateString) => {
    try {
      // Calculate the difference in days between start and end date
      const daysDifference = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
      
      // If more than 30 days, show month format, otherwise show day format
      if (daysDifference > 30) {
        return format(parseISO(dateString), "MMM yyyy");
      } else {
        return format(parseISO(dateString), "dd MMM");
      }
    } catch (error) {
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 text-primary"></div>
      </div>
    );
  }

  const stats = {
    revenue: {
      currentPeriod: dashboardData?.data?.currentMonthStats?.revenue || 0,
      changeFromPreviousPeriod:
        dashboardData?.data?.currentMonthStats?.revenuePercentageDifference ||
        0,
    },
    ordersCount: {
      currentPeriod: dashboardData?.data?.currentMonthStats?.orders || 0,
      changeFromPreviousPeriod:
        dashboardData?.data?.currentMonthStats?.ordersPercentageDifference || 0,
    },
    productsSold: {
      currentPeriod: dashboardData?.data?.topSellingProducts?.length || 0,
      changeFromPreviousPeriod: 0, // Not available in new API
    },
    unitsSold: {
      currentPeriod:
        dashboardData?.data?.topSellingProducts?.reduce(
          (sum, product) => sum + product.orders,
          0
        ) || 0,
      changeFromPreviousPeriod: 0, // Not available in new API
    },
  };

  const recentOrders = dashboardData?.data?.recentOrders || [];
  const lastTenDaysStats = {
    orders:
      dashboardData?.data?.orders?.map((order) => ({
        sale_date: order.date,
        total_orders: order.orders,
      })) || [],
    sales:
      dashboardData?.data?.orders?.map((order) => ({
        sale_date: order.date,
        total_sales: order.revenue,
      })) || [],
  };
  const topSellingProducts =
    dashboardData?.data?.topSellingProducts?.map((product) => ({
      productName: product.productName,
      unitSold: product.orders,
      revenueCollected: product.revenue,
    })) || [];
  const amazonAdsData = [
    {
      name: "Ad Spend",
      value: 0, // Not available in new API
    },
    {
      name: "Earnings",
      value: 0, // Not available in new API
    },
  ];
  const platformWiseSales =
    dashboardData?.data?.platformWiseSalesDistributionData?.map((platform) => ({
      platform: platform.platform,
      revenueCollected: platform.revenue,
      revenuePercentage: platform.percentage,
    })) || [];
  const stateWiseCurrentMonthStats =
    dashboardData?.data?.stateWiseSales?.map((state) => ({
      state: state.state,
      total_orders: state.orders,
      total_sales: state.revenue,
    })) || [];

  // Format chart data
  const daysDifference = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
  
  let chartData;
  if (daysDifference > 30) {
    // Group data by month for date ranges > 30 days
    const monthlyData = {};
    
    lastTenDaysStats.orders.forEach((item, index) => {
      const monthKey = format(parseISO(item.sale_date), "MMM yyyy");
      const sales = lastTenDaysStats.sales[index]?.total_sales || 0;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          name: monthKey,
          sales: 0,
          orders: 0,
        };
      }
      
      monthlyData[monthKey].sales += sales;
      monthlyData[monthKey].orders += item.total_orders;
    });
    
    chartData = Object.values(monthlyData).sort((a, b) => {
      return new Date(a.name) - new Date(b.name);
    });
  } else {
    // Use daily data for date ranges <= 30 days
    chartData = lastTenDaysStats.orders.map((item, index) => ({
      name: formatChartDate(item.sale_date),
      sales: lastTenDaysStats.sales[index]?.total_sales || 0,
      orders: item.total_orders,
    }));
  }

  // Check if we should show data labels (only if 15 or fewer data points)
  const shouldShowDataLabels = chartData.length <= 15;

  return (
    <div className="space-y-6">
      {/* Date Range Selector */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-semibold">Dashboard</h2>
        </div>
       
      </div>
    
      {/* Stats Grid */}
      {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Revenue"
          value={formatAmount(stats.revenue?.currentPeriod || 0)}
          icon={<FiCreditCard className="text-primary" size={24} />}
          trend={stats.revenue?.changeFromPreviousPeriod}
        />
        <StatCard
          title="Total Orders"
          value={stats.ordersCount?.currentPeriod || 0}
          icon={<FiShoppingBag className="text-primary" size={24} />}
          trend={stats.ordersCount?.changeFromPreviousPeriod}
        />
        <StatCard
          title="Products Sold"
          value={stats.productsSold?.currentPeriod || 0}
          icon={<FiPackage className="text-primary" size={24} />}
          trend={stats.productsSold?.changeFromPreviousPeriod}
        />
        <StatCard
          title="Units Sold"
          value={stats.unitsSold?.currentPeriod || 0}
          icon={<FiBox className="text-primary" size={24} />}
          trend={stats.unitsSold?.changeFromPreviousPeriod}
        />
      </div> */}
      {/* 
      {/* Today, Yesterday & Current Month Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="dashboard-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-800">
              Today's Performance
            </h3>
            <div className="p-2 bg-green-50 rounded-lg">
              <FiClock className="text-green-600" size={18} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <FiTrendingUp className="text-green-500 mr-1" size={14} />
                <p className="text-xs text-gray-600 font-medium">Net Sales</p>
              </div>
              <p className="text-lg font-bold text-black-600 my-1">
                {formatAmount(dashboardData?.data?.todayStats?.revenue || 0)}
              </p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <FiShoppingBag className="text-blue-500 mr-1" size={14} />
                <p className="text-xs text-gray-600 font-medium">Net Orders</p>
              </div>
              <p className="text-lg font-bold text-black-600 my-1">
                {dashboardData?.data?.todayStats?.orders || 0}
              </p>
            </div>
          </div>
        </div>
        <div className="dashboard-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-800">
              Yesterday's Performance
            </h3>
            <div className="p-2 bg-orange-50 rounded-lg">
              <FiCalendar className="text-orange-600" size={18} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <FiTrendingUp className="text-orange-500 mr-1" size={14} />
                <p className="text-xs text-gray-600 font-medium">Net Sales</p>
              </div>
              <p className="text-lg font-bold text-black-600 my-1">
                {formatAmount(
                  dashboardData?.data?.yesterdayStats?.revenue || 0
                )}
              </p>
              <p className="text-xs text-gray-500">
                {formatAmount(
                  dashboardData?.data?.yesterdayStats?.dayBeforeRevenue || 0
                )}

                <span
                  className={
                    dashboardData?.data?.yesterdayStats
                      ?.revenuePercentageDifference >= 0
                      ? "text-green-600"
                      : "text-red-600"
                  }
                >
                  (
                  {dashboardData?.data?.yesterdayStats
                    ?.revenuePercentageDifference >= 0
                    ? "+"
                    : ""}
                  {dashboardData?.data?.yesterdayStats?.revenuePercentageDifference?.toFixed(
                    1
                  ) || 0}
                  %)
                </span>
              </p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <FiShoppingBag className="text-orange-500 mr-1" size={14} />
                <p className="text-xs text-gray-600 font-medium">Net Orders</p>
              </div>
              <p className="text-lg font-bold text-black-600 my-1">
                {dashboardData?.data?.yesterdayStats?.orders || 0}
              </p>
              <p className="text-xs text-gray-500">
                {dashboardData?.data?.yesterdayStats?.dayBeforeOrders || 0}{" "}
                <span
                  className={
                    dashboardData?.data?.yesterdayStats
                      ?.ordersPercentageDifference >= 0
                      ? "text-green-600"
                      : "text-red-600"
                  }
                >
                  (
                  {dashboardData?.data?.yesterdayStats
                    ?.ordersPercentageDifference >= 0
                    ? "+"
                    : ""}
                  {dashboardData?.data?.yesterdayStats?.ordersPercentageDifference?.toFixed(
                    1
                  ) || 0}
                  %)
                </span>
              </p>
            </div>
          </div>
        </div>
        <div className="dashboard-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-800">
              Current Month Performance
            </h3>
            <div className="p-2 bg-purple-50 rounded-lg">
              <FiBarChart2 className="text-purple-600" size={18} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <FiTrendingUp className="text-purple-500 mr-1" size={14} />
                <p className="text-xs text-gray-600 font-medium">Net Sales</p>
              </div>
              <p className="text-lg font-bold text-black-600 my-1">
                {formatAmount(
                  dashboardData?.data?.currentMonthStats?.revenue || 0
                )}
              </p>
              <p className="text-xs text-gray-500">
                {formatAmount(
                  dashboardData?.data?.currentMonthStats?.monthBeforeRevenue ||
                    0
                )}{" "}
                <span
                  className={
                    dashboardData?.data?.currentMonthStats
                      ?.revenuePercentageDifference >= 0
                      ? "text-green-600"
                      : "text-red-600"
                  }
                >
                  (
                  {dashboardData?.data?.currentMonthStats
                    ?.revenuePercentageDifference >= 0
                    ? "+"
                    : ""}
                  {dashboardData?.data?.currentMonthStats?.revenuePercentageDifference?.toFixed(
                    1
                  ) || 0}
                  %)
                </span>
              </p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <FiShoppingBag className="text-purple-500 mr-1" size={14} />
                <p className="text-xs text-gray-600 font-medium">Net Orders</p>
              </div>
              <p className="text-lg font-bold text-black-600 my-1">
                {dashboardData?.data?.currentMonthStats?.orders || 0}
              </p>
              <p className="text-xs text-gray-500">
                {dashboardData?.data?.currentMonthStats?.monthBeforeOrders || 0}{" "}
                <span
                  className={
                    dashboardData?.data?.currentMonthStats
                      ?.ordersPercentageDifference >= 0
                      ? "text-green-600"
                      : "text-red-600"
                  }
                >
                  (
                  {dashboardData?.data?.currentMonthStats
                    ?.ordersPercentageDifference >= 0
                    ? "+"
                    : ""}
                  {dashboardData?.data?.currentMonthStats?.ordersPercentageDifference?.toFixed(
                    1
                  ) || 0}
                  %)
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Today's Order vs Cancel Ratio */}
        <div className="dashboard-card p-5  col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-800">
              Today's Order vs Cancel Ratio
            </h3>
            <div className="p-2 bg-red-50 rounded-lg">
              <FiShoppingBag className="text-red-600" size={18} />
            </div>
          </div>
          <div className="flex flex-col h-full">
            {/* Ratio Display */}
            <div className="flex justify-between items-center mb-4">
              <div className="text-center flex-1">
                <div className="flex items-center justify-center mb-1">
                  <FiShoppingBag className="text-green-500 mr-1" size={14} />
                  <p className="text-xs text-gray-600 font-medium">
                    Today's Orders
                  </p>
                </div>
                <p className="text-lg font-bold text-green-600">
                  {(dashboardData?.data?.todayStats?.orders || 0) +
                    (dashboardData?.data?.cancelledOrdersStats?.today || 0)}
                </p>
              </div>
              <div className="text-center flex-1">
                <div className="flex items-center justify-center mb-1">
                  <FiShoppingBag className="text-red-500 mr-1" size={14} />
                  <p className="text-xs text-gray-600 font-medium">
                    Today's Cancelled
                  </p>
                </div>
                <p className="text-lg font-bold text-red-600">
                  {dashboardData?.data?.cancelledOrdersStats?.today || 0}
                </p>
              </div>
            </div>

            {/* Visual Ratio Bar - Centered in remaining space */}
            <div className="flex-1 flex flex-col justify-center">
              <div className="w-full">
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>Success Rate</span>
                  <span>
                    {dashboardData?.data?.todayStats?.orders &&
                    dashboardData?.data?.cancelledOrdersStats?.today
                      ? (
                          ((dashboardData.data.todayStats.orders -
                            dashboardData.data.cancelledOrdersStats.today) /
                            dashboardData.data.todayStats.orders) *
                          100
                        ).toFixed(1)
                      : 100}
                    %
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${
                        dashboardData?.data?.todayStats?.orders &&
                        dashboardData?.data?.cancelledOrdersStats?.today
                          ? ((dashboardData.data.todayStats.orders -
                              dashboardData.data.cancelledOrdersStats.today) /
                              dashboardData.data.todayStats.orders) *
                            100
                          : 100
                      }%`,
                    }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>
                    Successful:{" "}
                    {dashboardData?.data?.todayStats?.orders -
                      (dashboardData?.data?.cancelledOrdersStats?.today || 0) ||
                      0}
                  </span>
                  <span>
                    Cancelled:{" "}
                    {dashboardData?.data?.cancelledOrdersStats?.today || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Cancelled & Returned Orders Stats */}
        <div className="space-y-4 col-span-1">
          <div className="dashboard-card p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-800">
                Cancelled Orders
              </h3>
              <div className="p-2 bg-red-50 rounded-lg">
                <FiShoppingBag className="text-red-600" size={18} />
              </div>
            </div>
            <div className="flex justify-between items-center">
              <div className="text-center flex-1">
                <div className="flex items-center justify-center mb-1">
                  <FiCalendar className="text-red-500 mr-1" size={14} />
                  <p className="text-xs text-gray-600 font-medium">Today</p>
                </div>
                <p className="text-lg font-bold text-red-600">
                  {dashboardData?.data?.cancelledOrdersStats?.today || 0}
                </p>
              </div>
              <div className="text-center flex-1">
                <div className="flex items-center justify-center mb-1">
                  <FiCalendar className="text-orange-500 mr-1" size={14} />
                  <p className="text-xs text-gray-600 font-medium">Yesterday</p>
                </div>
                <p className="text-lg font-bold text-orange-600">
                  {dashboardData?.data?.cancelledOrdersStats?.yesterday || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="dashboard-card p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-800">
                Returned Orders
              </h3>
              <div className="p-2 bg-yellow-50 rounded-lg">
                <FiShoppingBag className="text-yellow-600" size={18} />
              </div>
            </div>
            <div className="flex justify-between items-center">
              <div className="text-center flex-1">
                <div className="flex items-center justify-center mb-1">
                  <FiCalendar className="text-yellow-500 mr-1" size={14} />
                  <p className="text-xs text-gray-600 font-medium">Today</p>
                </div>
                <p className="text-lg font-bold text-yellow-600">
                  {dashboardData?.data?.returnedOrdersStats?.today || 0}
                </p>
              </div>
              <div className="text-center flex-1">
                <div className="flex items-center justify-center mb-1">
                  <FiCalendar className="text-orange-500 mr-1" size={14} />
                  <p className="text-xs text-gray-600 font-medium">Yesterday</p>
                </div>
                <p className="text-lg font-bold text-orange-600">
                  {dashboardData?.data?.returnedOrdersStats?.yesterday || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Select Date Range */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-semibold">Select Date Range: </h2>
          <div className="flex items-center gap-2">
            <div className="relative">
              <select
                className="select select-bordered select-sm w-64"
                value={selectedDateRange}
                onChange={(e) =>
                  handleDateRangeChange(parseInt(e.target.value))
                }
              >
                {dateRangeOptions.map((option, index) => (
                  <option key={index} value={index}>
                    {option.label}
                  </option>
                ))}
              </select>
              {/* <FiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-400" /> */}
            </div>
            {selectedDateRange === 7 && (
              <button
                className="btn btn-primary btn-sm"
                onClick={() => {
                  setCustomStartDate(appliedCustomStartDate);
                  setCustomEndDate(appliedCustomEndDate);
                  setShowCustomDateModal(true);
                }}
              >
                Edit Custom Range
              </button>
            )}
          </div>
        </div>
        <div className="text-sm text-gray-500">
          {format(startDate, "MMM dd, yyyy")} -{" "}
          {format(endDate, "MMM dd, yyyy")}
        </div>
      </div>
            {/* Custom Date Range Modal */}
            {showCustomDateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">
              Select Custom Date Range
            </h3>
            <div className="space-y-4">
              <div>
                <label className="label">
                  <span className="label-text">Start Date</span>
                </label>
                <DatePicker
                  selected={customStartDate}
                  onChange={setCustomStartDate}
                  className="input input-bordered w-full"
                  dateFormat="yyyy-MM-dd"
                  maxDate={customEndDate}
                  minDate={subDays(customEndDate, 90)}
                />
              </div>
              <div>
                <label className="label">
                  <span className="label-text">End Date</span>
                </label>
                <DatePicker
                  selected={customEndDate}
                  onChange={setCustomEndDate}
                  className="input input-bordered w-full"
                  dateFormat="yyyy-MM-dd"
                  minDate={customStartDate}
                  maxDate={new Date()}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                className="btn btn-outline btn-sm"
                onClick={() => setShowCustomDateModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleCustomDateSubmit}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales Chart */}
        <div className="dashboard-card p-6">
          <h3 className="text-lg font-semibold mb-4">Sales Overview</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart 
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis 
                  tick={{ fontSize: 14, fill: "#6b7280" }}
                  tickFormatter={(value) => formatAmount(value)}
                />
                <Tooltip
                  formatter={(value) => [formatAmount(value), "Sales"]}
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #ccc",
                  }}
                />
                <Line type="monotone" dataKey="sales" stroke="#0ea5e9">
                  {shouldShowDataLabels && (
                    <LabelList 
                      dataKey="sales" 
                      position="top" 
                      formatter={(value, name, props) => {
                        const index = props.index;
                        return formatAmount(value);
                      }}
                      content={(props) => {
                        const { x, y, value, index } = props;
                        if (!value) return null;
                        
                        // Alternate between top and bottom positions
                        const isEven = index % 2 === 0;
                        const offset = isEven ? -15 : 15;
                        
                        return (
                          <text
                            x={x}
                            y={y + offset}
                            textAnchor="middle"
                            dominantBaseline={isEven ? "baseline" : "hanging"}
                            fill="#374151"
                            fontSize="12"
                            fontWeight="bold"
                          >
                            {formatAmount(value)}
                          </text>
                        );
                      }}
                    />
                  )}
                </Line>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Orders Chart */}
        <div className="dashboard-card p-6">
          <h3 className="text-lg font-semibold mb-4">Orders Overview</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tick={{ fontSize: 14, fill: "#6b7280" }} />
                <Tooltip
                  formatter={(value) => [value, "Orders"]}
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #ccc",
                  }}
                />
                <Bar dataKey="orders" fill="#0ea5e9">
                  {shouldShowDataLabels && (
                    <LabelList 
                      dataKey="orders" 
                      position="center" 
                      formatter={(value) => value}
                      content={(props) => {
                        const { x, y, width, height, value } = props;
                        if (!value) return null;
                        
                        // If bar is too small (less than 30px height), show label on top
                        const isBarTooSmall = height < 30;
                        
                        return (
                          <text
                            x={x + width / 2}
                            y={isBarTooSmall ? y - 5 : y + height / 2}
                            textAnchor="middle"
                            dominantBaseline={isBarTooSmall ? "baseline" : "middle"}
                            fill={isBarTooSmall ? "black" : "white"}
                            fontSize="12"
                            fontWeight="bold"
                            transform={isBarTooSmall ? "" : `rotate(-90, ${x + width / 2}, ${y + height / 2})`}
                          >
                            {value}
                          </text>
                        );
                      }}
                    />
                  )}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>


      
      {/* Top Products and India Map */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
        <div className="dashboard-card p-6">
          <TopSellingProducts
            data={topSellingProducts}
            startDate={startDate}
            endDate={endDate}
          />
        </div>
        <div className="grid grid-rows-2 gap-6">
          <div className="dashboard-card p-6">
            <IndiaMapComponent
              dataType={dataType}
              setDataType={setDataType}
              stateData={stateWiseCurrentMonthStats}
            />
          </div>

          <div className="dashboard-card p-6">
            <PlatformSales data={platformWiseSales} />
          </div>
        </div>
      </div>
      {/* Cancelled and Returned Orders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cancelled Orders */}
        <div className="dashboard-card p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Cancelled Orders</h3>
            <button
              className="btn btn-primary btn-sm"
              onClick={() =>
                navigate("/orders", {
                  state: {
                    startDate: format(startDate, "yyyy-MM-dd"),
                    endDate: format(endDate, "yyyy-MM-dd"),
                    status: "cancelled",
                  },
                })
              }
            >
              View More
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="table table-zebra w-full">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Order Date</th>
                  <th>Customer</th>
                  <th>Product</th>
                  <th>Platform</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData?.data?.cancelledOrders
                  ?.slice(0, 5)
                  .map((order) => (
                    <tr key={order.id}>
                      <td>
                        <span
                          className="cursor-pointer text-blue-500 hover:text-blue-600 transition-colors"
                          onClick={() =>
                            navigate(`/orders/detail/${order.refNo}`)
                          }
                        >
                          #{order.id}
                        </span>
                        <br /> ({order.refNo})
                      </td>
                      <td className="whitespace-nowrap">
                        {formatDate(order.refDate)}
                      </td>
                      <td>{order.customerName || "N/A"}</td>
                      <td>
                        <span
                          className="line-clamp-1 block cursor-pointer hover:text-blue-600 transition-colors"
                          style={{
                            maxWidth: 150,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: "vertical",
                            whiteSpace: "normal",
                          }}
                          title={order.productName}
                          onClick={() => {
                            const skuForUrl = order.sku || order.channelSku || order.productName;
                            navigate(
                              `/analytics/product-detail/${encodeURIComponent(
                                skuForUrl
                              )}`,
                              {
                                state: {
                                  productName: order.productName,
                                  useSku: !!(order.sku || order.channelSku),
                                  startDate:
                                    startDate.toLocaleDateString("en-CA"),
                                  endDate: endDate.toLocaleDateString("en-CA"),
                                  fromPage: "/dashboard",
                                },
                              }
                            );
                          }}
                        >
                          {order.productName}
                        </span>
                      </td>
                      <td>
                        <span className="badge badge-outline">
                          {order.postingAccount}
                        </span>
                      </td>
                      <td>{formatAmountLegacy(order.netAmount)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {(!dashboardData?.data?.cancelledOrders ||
              dashboardData.data.cancelledOrders.length === 0) && (
              <div className="text-center py-4 text-gray-500">
                No cancelled orders found
              </div>
            )}
          </div>
        </div>

        {/* Returned Orders */}
        <div className="dashboard-card p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Returned Orders</h3>
            <button
              className="btn btn-primary btn-sm"
              onClick={() =>
                navigate("/orders", {
                  state: {
                    startDate: format(startDate, "yyyy-MM-dd"),
                    endDate: format(endDate, "yyyy-MM-dd"),
                    status: "returned",
                  },
                })
              }
            >
              View More
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="table table-zebra w-full">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Order Date</th>
                  <th>Customer</th>
                  <th>Product</th>
                  <th>Platform</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData?.data?.returnedOrders
                  ?.slice(0, 5)
                  .map((order) => (
                    <tr key={order.id}>
                      <td>
                        <span
                          className="cursor-pointer text-blue-500 hover:text-blue-600 transition-colors"
                          onClick={() =>
                            navigate(`/orders/detail/${order.refNo}`)
                          }
                        >
                          #{order.id}
                        </span>
                        <br /> ({order.refNo})
                      </td>
                      <td className="whitespace-nowrap">
                        {formatDate(order.refDate)}
                      </td>
                      <td>{order.customerName || "N/A"}</td>
                      <td>
                        <span
                          className="line-clamp-1 block cursor-pointer hover:text-blue-600 transition-colors"
                          style={{
                            maxWidth: 150,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: "vertical",
                            whiteSpace: "normal",
                          }}
                          title={order.productName}
                          onClick={() => {
                            const skuForUrl = order.sku || order.channelSku || order.productName;
                            navigate(
                              `/analytics/product-detail/${encodeURIComponent(
                                skuForUrl
                              )}`,
                              {
                                state: {
                                  productName: order.productName,
                                  useSku: !!(order.sku || order.channelSku),
                                  startDate:
                                    startDate.toLocaleDateString("en-CA"),
                                  endDate: endDate.toLocaleDateString("en-CA"),
                                  fromPage: "/dashboard",
                                },
                              }
                            );
                          }}
                        >
                          {order.productName}
                        </span>
                      </td>
                      <td>
                        <span className="badge badge-outline">
                          {order.postingAccount}
                        </span>
                      </td>
                      <td>{formatAmountLegacy(order.netAmount)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {(!dashboardData?.data?.returnedOrders ||
              dashboardData.data.returnedOrders.length === 0) && (
              <div className="text-center py-4 text-gray-500">
                No returned orders found
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Recent Orders */}
      <div className="dashboard-card p-6">
        <h3 className="text-lg font-semibold mb-4">Recent Orders</h3>
        <div className="overflow-x-auto">
          <table className="table w-full">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Order Date</th>

                <th>Customer</th>
                <th>Product</th>
                <th>Platform</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {recentOrders.map((order) => (
                <tr key={order.id}>
                  <td className="whitespace-nowrap">
                    <span
                      className="whitespace-nowrap cursor-pointer text-blue-500 hover:text-blue-600 transition-colors"
                      onClick={() => navigate(`/orders/detail/${order.refNo}`)}
                    >
                      #{order.id}
                    </span>
                    <br /> ({order.refNo})
                  </td>
                  <td className="whitespace-nowrap">
                    {formatDate(order.refDate)}
                  </td>

                  <td>{order.customerName || "N/A"}</td>
                  <td>
                    <span
                      className="line-clamp-1 block cursor-pointer hover:text-blue-600 transition-colors"
                      style={{
                        maxWidth: 180,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: "vertical",
                        whiteSpace: "normal",
                      }}
                      title={order.productName}
                      onClick={() => {
                        const skuForUrl = order.sku || order.channelSku || order.productName;
                        navigate(
                          `/analytics/product-detail/${encodeURIComponent(
                            skuForUrl
                          )}`,
                          {
                            state: {
                              productName: order.productName,
                              useSku: !!(order.sku || order.channelSku),
                              startDate: startDate.toLocaleDateString("en-CA"),
                              endDate: endDate.toLocaleDateString("en-CA"),
                              fromPage: "/dashboard",
                            },
                          }
                        );
                      }}
                    >
                      {order.productName}
                    </span>
                  </td>
                  <td>
                    <span className="badge badge-outline">
                      {order.postingAccount}
                    </span>
                  </td>
                  <td>{formatAmountLegacy(order.netAmount)}</td>
                  <td>
                    <span
                      className={`badge ${
                        order.status === "delivered"
                          ? "badge-success text-white"
                          : order.status === "processing"
                          ? "badge-warning text-gray-900"
                          : order.status === "cancelled"
                          ? "badge-error text-white"
                          : order.status === "returned"
                          ? "badge-secondary text-white"
                          : "badge-info text-white"
                      }`}
                    >
                      {order.status.charAt(0).toUpperCase() +
                        order.status.slice(1)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>{" "}
    </div>
  );
};

export default Dashboard;
